import { AlertTriangle, Calendar, FileText, ChevronRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useDocumentosPorVencer, useObtenerUrlDocumento } from '@/hooks/useDocumentos'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import type { DocumentoConCliente } from '@/types/database'
import { CATEGORIAS_DOCUMENTO } from '@/types/documentos'

interface DocumentosPorVencerProps {
  diasLimite?: number
  maxItems?: number
  className?: string
  onVerTodos?: () => void
}

function getUrgenciaConfig(diasRestantes: number | null) {
  if (diasRestantes === null) return null

  if (diasRestantes <= 0) {
    return {
      label: 'Vencido',
      bgClass: 'bg-red-100 dark:bg-red-950',
      textClass: 'text-red-700 dark:text-red-400',
      borderClass: 'border-red-200 dark:border-red-900',
    }
  }
  if (diasRestantes <= 7) {
    return {
      label: `${diasRestantes}d`,
      bgClass: 'bg-red-50 dark:bg-red-950/50',
      textClass: 'text-red-600 dark:text-red-400',
      borderClass: 'border-red-100 dark:border-red-900/50',
    }
  }
  if (diasRestantes <= 15) {
    return {
      label: `${diasRestantes}d`,
      bgClass: 'bg-orange-50 dark:bg-orange-950/50',
      textClass: 'text-orange-600 dark:text-orange-400',
      borderClass: 'border-orange-100 dark:border-orange-900/50',
    }
  }
  if (diasRestantes <= 30) {
    return {
      label: `${diasRestantes}d`,
      bgClass: 'bg-yellow-50 dark:bg-yellow-950/50',
      textClass: 'text-yellow-600 dark:text-yellow-400',
      borderClass: 'border-yellow-100 dark:border-yellow-900/50',
    }
  }
  return null
}

function DocumentoItem({ documento }: { documento: DocumentoConCliente }) {
  const obtenerUrl = useObtenerUrlDocumento()
  const urgencia = getUrgenciaConfig(documento.dias_restantes)
  const categoriaConfig = documento.categoria
    ? CATEGORIAS_DOCUMENTO[documento.categoria]
    : null

  const handleClick = async () => {
    try {
      const url = await obtenerUrl.mutateAsync(documento.archivo_path)
      window.open(url, '_blank')
    } catch (error) {
      console.error('Error al abrir documento:', error)
      toast.error('Error al abrir el documento')
    }
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        'w-full text-left p-3 rounded-lg border transition-colors hover:bg-muted/50',
        urgencia?.borderClass || 'border-border'
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'p-2 rounded-md',
            urgencia?.bgClass || 'bg-muted'
          )}
        >
          <FileText className={cn('h-4 w-4', urgencia?.textClass || 'text-muted-foreground')} />
        </div>

        <div className="flex-1 min-w-0 space-y-1">
          <p className="font-medium text-sm truncate">{documento.nombre}</p>

          {documento.cliente_nombre && (
            <p className="text-xs text-muted-foreground truncate">
              {documento.cliente_nombre}
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            {categoriaConfig && (
              <Badge variant="secondary" className="text-xs">
                {categoriaConfig.label}
              </Badge>
            )}

            {urgencia && (
              <Badge
                variant="secondary"
                className={cn('text-xs', urgencia.bgClass, urgencia.textClass)}
              >
                {documento.dias_restantes !== null && documento.dias_restantes <= 0 && (
                  <AlertTriangle className="h-3 w-3 mr-1" />
                )}
                {urgencia.label}
              </Badge>
            )}
          </div>

          {documento.fecha_vencimiento && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(documento.fecha_vencimiento).toLocaleDateString('es-AR', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            </p>
          )}
        </div>

        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </div>
    </button>
  )
}

export function DocumentosPorVencer({
  diasLimite = 30,
  maxItems = 10,
  className,
  onVerTodos,
}: DocumentosPorVencerProps) {
  const { data: documentos = [], isLoading } = useDocumentosPorVencer(diasLimite)

  const documentosMostrar = documentos.slice(0, maxItems)
  const hayMas = documentos.length > maxItems

  // Contar por urgencia
  const vencidos = documentos.filter((d) => d.dias_restantes !== null && d.dias_restantes <= 0).length
  const urgentes = documentos.filter(
    (d) => d.dias_restantes !== null && d.dias_restantes > 0 && d.dias_restantes <= 7
  ).length
  const proximos = documentos.filter(
    (d) => d.dias_restantes !== null && d.dias_restantes > 7
  ).length

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          Documentos por Vencer
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Resumen de urgencia */}
        {documentos.length > 0 && (
          <div className="flex gap-2 text-xs">
            {vencidos > 0 && (
              <Badge variant="destructive" className="text-xs">
                {vencidos} vencido{vencidos !== 1 ? 's' : ''}
              </Badge>
            )}
            {urgentes > 0 && (
              <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">
                {urgentes} urgente{urgentes !== 1 ? 's' : ''}
              </Badge>
            )}
            {proximos > 0 && (
              <Badge variant="secondary" className="text-xs">
                {proximos} próximo{proximos !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        )}

        {/* Lista de documentos */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : documentos.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No hay documentos por vencer</p>
            <p className="text-xs">en los próximos {diasLimite} días</p>
          </div>
        ) : (
          <ScrollArea className="h-[300px] -mx-2 px-2">
            <div className="space-y-2">
              {documentosMostrar.map((documento) => (
                <DocumentoItem key={documento.id} documento={documento} />
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Ver todos */}
        {hayMas && onVerTodos && (
          <Button variant="ghost" className="w-full text-sm" onClick={onVerTodos}>
            Ver todos ({documentos.length})
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
